import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/lib/auth";
import { HFButton } from "@/components/ui-hf";
import { Lock } from "lucide-react";

export default function AdminLoginPage() {
    const { status, login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [remember, setRemember] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>("");
    const location = useLocation();
    const from =
        (location.state as { from?: string } | null)?.from || "/admin/dashboard";

    if (status === "authed") {
        return <Navigate to={from} replace />;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        const res = await login(email.trim(), password);
        setBusy(false);
        if (!res.ok) {
            setError(res.error || "Login fehlgeschlagen.");
        }
    };

    const input =
        "w-full px-4 py-3 rounded-sm bg-white border text-sm text-hf-ink placeholder:text-hf-muted focus:outline-none focus:ring-2 focus:ring-[hsl(var(--hf-gold))] focus:border-transparent transition";
    const label = "block text-sm text-hf-ink mb-1.5 font-medium";

    return (
        <div
            className="min-h-screen flex items-center justify-center px-6 py-14"
            style={{ background: "hsl(var(--hf-surface))" }}
        >
            {/* Admin-Login niemals von Suchmaschinen indexieren lassen. */}
            <Helmet>
                <title>Anmelden · HypeFly Admin</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <div
                className="w-full max-w-md p-8 md:p-10 rounded-sm"
                style={{
                    background: "hsl(var(--hf-bg))",
                    border: "1px solid hsl(var(--hf-line))",
                    boxShadow: "var(--shadow-warm-lg)",
                }}
                data-testid="admin-login-card"
            >
                <div
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full"
                    style={{
                        background: "hsl(var(--hf-ink))",
                        color: "hsl(var(--hf-gold-soft))",
                    }}
                >
                    <Lock size={20} />
                </div>
                <div className="hf-eyebrow mt-5">HypeFly · Admin</div>
                <h1 className="font-display text-3xl mt-2">Anmelden</h1>
                <p className="mt-2 text-sm text-hf-muted">
                    Bitte melde dich mit deinen Admin-Zugangsdaten an.
                </p>

                <form className="mt-7 space-y-4" onSubmit={handleSubmit} noValidate>
                    <div>
                        <label htmlFor="login-email" className={label}>
                            E-Mail
                        </label>
                        <input
                            id="login-email"
                            type="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={input}
                            style={{ borderColor: "hsl(var(--hf-line))" }}
                            data-testid="admin-login-email"
                        />
                    </div>
                    <div>
                        <label htmlFor="login-password" className={label}>
                            Passwort
                        </label>
                        <input
                            id="login-password"
                            type="password"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={input}
                            style={{ borderColor: "hsl(var(--hf-line))" }}
                            data-testid="admin-login-password"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-hf-ink">
                        <input
                            type="checkbox"
                            checked={remember}
                            onChange={(e) => setRemember(e.target.checked)}
                            className="accent-[hsl(var(--hf-gold-dark))]"
                            data-testid="admin-login-remember"
                        />
                        <span>Angemeldet bleiben</span>
                    </label>
                    {error && (
                        <div
                            className="p-3 rounded text-sm"
                            style={{
                                background: "hsl(var(--hf-danger) / 0.12)",
                                color: "hsl(var(--hf-danger))",
                                border: "1px solid hsl(var(--hf-danger) / 0.3)",
                            }}
                            data-testid="admin-login-error"
                        >
                            {error}
                        </div>
                    )}
                    <HFButton
                        type="submit"
                        disabled={busy}
                        variant="primary"
                        trailing={<span aria-hidden>→</span>}
                        className="w-full justify-center"
                        data-testid="admin-login-submit"
                    >
                        {busy ? "Wird angemeldet…" : "Anmelden"}
                    </HFButton>
                </form>
                <p className="mt-8 text-xs text-hf-muted">
                    Zurück zur{" "}
                    <a href="/" className="underline hover:text-hf-gold-dark">
                        Website
                    </a>
                    .
                </p>
            </div>
        </div>
    );
}
